import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './AccountingVouchersImportPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: "Muhasebe Fisleri - Excel'den Aktarim",
    description: 'Sirket ve donemi secip Excel dosyasi yukleyerek muhasebe fislerini toplu olarak import edin.',
    companiesLoadError: 'Sirketler yuklenemedi.',
    periodsLoadError: 'Donemler yuklenemedi.',
    rulesLoadError: 'Validasyon kurallari alinamadi.',
    importStatusLoadError: 'Import durumu alinamadi.',
    templateDownloadError: 'Sablon indirilemedi.',
    invalidServerResponse: 'Sunucudan gecersiz cevap alindi.',
    serverError: 'Sunucu hatasi ({{status}})',
    connectionError: 'Sunucuya baglanirken hata olustu.',
    selectExcelFile: 'Lutfen bir Excel dosyasi secin.',
    selectCompany: 'Lutfen bir sirket secin.',
    selectPeriod: 'Lutfen bir donem secin.',
    companyAndPeriodRequired: 'Sirket ve donem secilmelidir.',
    clearConfirm: 'Excel import edilen fisleri silmek istediginize emin misiniz?',
    clearFailed: 'Veriler temizlenemedi.',
    uploadButton: 'Excel Yukle',
    uploadingButton: 'Excel Yukleniyor...',
    downloadTemplate: 'Sablonu Indir',
    clearData: 'Verileri Temizle',
    clearingData: 'Temizleniyor...',
    companyLabel: 'Sirket secin',
    companyPlaceholder: 'Sirket secin...',
    periodLabel: 'Donem secin',
    periodPlaceholder: 'Donem secin...',
    fileLabel: 'Excel dosyasi secin',
    validationWarnings: 'Validasyon Uyarilari',
    importStatus: 'Import Durumu',
    totalVouchers: 'Toplam fis',
    draft: 'Taslak',
    approved: 'Onayli',
    posted: 'Post edilmis',
    lastImport: 'Son import',
    validationRules: 'Validasyon Kurallari',
    requiredColumns: 'Zorunlu sutunlar',
    optionalColumns: 'Optional sutunlar',
    rules: 'Kurallar',
    maxFileSize: 'Maksimum dosya boyutu',
    supportedFormats: 'Desteklenen formatlar',
    importedSummary: '{{inserted}} fis iceri alindi, {{skipped}} fis atlandi.',
    clearedSummary: '{{deleted}} fis temizlendi.',
    active: 'Aktif',
    passive: 'Pasif',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: 'Accounting Vouchers - Import from Excel',
    description: 'Select company and period, then bulk import accounting vouchers from an Excel file.',
    companiesLoadError: 'Companies could not be loaded.',
    periodsLoadError: 'Periods could not be loaded.',
    rulesLoadError: 'Validation rules could not be fetched.',
    importStatusLoadError: 'Import status could not be fetched.',
    templateDownloadError: 'Template could not be downloaded.',
    invalidServerResponse: 'Invalid response received from server.',
    serverError: 'Server error ({{status}})',
    connectionError: 'An error occurred while connecting to server.',
    selectExcelFile: 'Please select an Excel file.',
    selectCompany: 'Please select a company.',
    selectPeriod: 'Please select a period.',
    companyAndPeriodRequired: 'Company and period must be selected.',
    clearConfirm: 'Are you sure you want to delete imported vouchers from Excel?',
    clearFailed: 'Data could not be cleared.',
    uploadButton: 'Upload Excel',
    uploadingButton: 'Uploading Excel...',
    downloadTemplate: 'Download Template',
    clearData: 'Clear Data',
    clearingData: 'Clearing...',
    companyLabel: 'Select company',
    companyPlaceholder: 'Select company...',
    periodLabel: 'Select period',
    periodPlaceholder: 'Select period...',
    fileLabel: 'Select Excel file',
    validationWarnings: 'Validation Warnings',
    importStatus: 'Import Status',
    totalVouchers: 'Total vouchers',
    draft: 'Draft',
    approved: 'Approved',
    posted: 'Posted',
    lastImport: 'Last import',
    validationRules: 'Validation Rules',
    requiredColumns: 'Required columns',
    optionalColumns: 'Optional columns',
    rules: 'Rules',
    maxFileSize: 'Maximum file size',
    supportedFormats: 'Supported formats',
    importedSummary: '{{inserted}} vouchers imported, {{skipped}} vouchers skipped.',
    clearedSummary: '{{deleted}} vouchers cleared.',
    active: 'Active',
    passive: 'Passive',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const mapStatusLabel = (raw, isEnglish, t) => {
  if (!isEnglish) return raw;
  if (raw === 'Aktif') return t('active');
  if (raw === 'Pasif') return t('passive');
  return raw;
};

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

function AccountingVouchersImportPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const isEnglish = localeKey === 'en-US';
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [companies, setCompanies] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(null);
  const [validationRules, setValidationRules] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [clearPending, setClearPending] = useState(false);
  const fileInputRef = useRef(null);
  const { canWrite } = useCompanyWriteAccess(selectedCompanyId);

  const companyName = useMemo(() => companies.find((company) => String(company.id) === selectedCompanyId)?.name, [companies, selectedCompanyId]);

  const periodLabel = useMemo(() => {
    const period = periods.find((item) => String(item.id) === selectedPeriodId);
    return period ? `${period.code}${period.status ? ` (${mapStatusLabel(period.status, isEnglish, t)})` : ''}` : '';
  }, [periods, selectedPeriodId, isEnglish, t]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('companiesLoadError'));
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('companiesLoadError') });
    }
  }, [t]);

  const fetchPeriods = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/periods`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('periodsLoadError'));
      const data = await response.json();
      setPeriods(data);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('periodsLoadError') });
    }
  }, [t]);

  const fetchValidationRules = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounting/vouchers/import-validation-rules`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('rulesLoadError'));
      const data = await response.json();
      setValidationRules(data);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('rulesLoadError') });
    }
  }, [t]);

  const fetchImportStatus = useCallback(async () => {
    if (!selectedCompanyId || !selectedPeriodId) {
      setImportStatus(null);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounting/vouchers/import-status?companyId=${selectedCompanyId}&periodId=${selectedPeriodId}`,
        { headers: buildHeaders() }
      );
      if (!response.ok) throw new Error(t('importStatusLoadError'));
      const data = await response.json();
      setImportStatus(data);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('importStatusLoadError') });
    }
  }, [selectedCompanyId, selectedPeriodId, t]);

  useEffect(() => {
    fetchCompanies();
    fetchPeriods();
    fetchValidationRules();
  }, [fetchCompanies, fetchPeriods, fetchValidationRules]);

  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) setSelectedCompanyId(String(companies[0].id));
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(String(periods[0].id));
  }, [periods, selectedPeriodId]);

  useEffect(() => {
    fetchImportStatus();
  }, [fetchImportStatus]);

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
    setStatusMessage(null);
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounting/vouchers/import-templates?templateType=standard`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('templateDownloadError'));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'accounting_voucher_import_template_standard.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('templateDownloadError') });
    }
  };

  const uploadWithProgress = (formData) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/accounting/vouchers/import-excel`);
    const headers = buildHeaders();
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) setUploadProgress(Math.round((event.loaded * 100) / event.total));
    };

    xhr.onload = () => {
      let payload = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch (_error) {
        reject(new Error(t('invalidServerResponse')));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload);
      } else {
        reject(new Error(payload?.message || t('serverError').replace('{{status}}', String(xhr.status))));
      }
    };

    xhr.onerror = () => reject(new Error(t('connectionError')));
    xhr.send(formData);
  });

  const handleUpload = async () => {
    if (!canWrite) {
      setStatusMessage({ type: 'error', text: t('readOnlyAccess') });
      return;
    }
    if (!file) {
      setStatusMessage({ type: 'error', text: t('selectExcelFile') });
      return;
    }
    if (!selectedCompanyId) {
      setStatusMessage({ type: 'error', text: t('selectCompany') });
      return;
    }
    if (!selectedPeriodId) {
      setStatusMessage({ type: 'error', text: t('selectPeriod') });
      return;
    }

    setIsUploading(true);
    setStatusMessage(null);
    setImportResult(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', selectedCompanyId);
    formData.append('periodId', selectedPeriodId);
    formData.append('overwrite', 'true');

    try {
      const payload = await uploadWithProgress(formData);
      setImportResult(payload);
      setStatusMessage({
        type: 'success',
        text: t('importedSummary')
          .replace('{{inserted}}', String(payload.inserted))
          .replace('{{skipped}}', String(payload.skipped))
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchImportStatus();
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('connectionError') });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClearData = async () => {
    if (!canWrite) {
      setStatusMessage({ type: 'error', text: t('readOnlyAccess') });
      return;
    }
    if (!selectedCompanyId || !selectedPeriodId) {
      setStatusMessage({ type: 'error', text: t('companyAndPeriodRequired') });
      return;
    }
    if (!window.confirm(t('clearConfirm'))) return;

    setClearPending(true);
    setStatusMessage(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounting/vouchers/clear-excel-import?companyId=${selectedCompanyId}&periodId=${selectedPeriodId}`,
        { method: 'DELETE', headers: buildHeaders() }
      );

      if (!response.ok) throw new Error(t('clearFailed'));

      const payload = await response.json();
      setStatusMessage({
        type: 'success',
        text: t('clearedSummary').replace('{{deleted}}', String(payload.deleted))
      });
      setImportResult(null);
      fetchImportStatus();
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || t('clearFailed') });
    } finally {
      setClearPending(false);
    }
  };

  return (
    <div className="voucher-page">
      <PageHeader title={t('title')} description={t('description')} />

      <div className="voucher-grid">
        <div className="voucher-card">
          <div className="voucher-actions">
            <button type="button" className="btn-primary" onClick={handleUpload} disabled={!canWrite || isUploading || !file}>
              {isUploading ? t('uploadingButton') : t('uploadButton')}
            </button>
            <button type="button" className="btn-secondary" onClick={handleTemplateDownload}>{t('downloadTemplate')}</button>
            <button type="button" className="btn-danger" onClick={handleClearData} disabled={!canWrite || isUploading || clearPending}>
              {clearPending ? t('clearingData') : t('clearData')}
            </button>
          </div>

          <div className="voucher-form">
            <div className="voucher-field">
              <label>{t('companyLabel')}</label>
              <select value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)} disabled={isUploading}>
                <option value="">{t('companyPlaceholder')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div className="voucher-field">
              <label>{t('periodLabel')}</label>
              <select value={selectedPeriodId} onChange={(event) => setSelectedPeriodId(event.target.value)} disabled={isUploading}>
                <option value="">{t('periodPlaceholder')}</option>
                {periods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.code} {period.description ? `- ${period.description}` : ''} ({mapStatusLabel(period.status, isEnglish, t)})
                  </option>
                ))}
              </select>
            </div>
            <div className="voucher-field">
              <label>{t('fileLabel')}</label>
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} disabled={isUploading} ref={fileInputRef} />
              {file && <span className="file-note">{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>}
            </div>
          </div>

          {isUploading && (
            <div className="progress-bar">
              <div className="progress-track"><div className="progress-filled" style={{ width: `${uploadProgress}%` }} /></div>
              <span>{uploadProgress}%</span>
            </div>
          )}

          {statusMessage && (
            <div className={`alert ${statusMessage.type === 'error' ? 'alert-error' : 'alert-success'}`}>
              <p>{statusMessage.text}{companyName ? ` (${companyName}${periodLabel ? ` - ${periodLabel}` : ''})` : ''}</p>
            </div>
          )}

          {importResult?.warnings?.length > 0 && (
            <div className="alert alert-warning">
              <h4>{t('validationWarnings')}</h4>
              <ul>
                {importResult.warnings.map((warning) => (<li key={warning}>{warning}</li>))}
              </ul>
            </div>
          )}
        </div>

        <div className="voucher-info">
          {importStatus && (
            <div className="info-card">
              <h3>{t('importStatus')}</h3>
              <div className="info-grid">
                <div><p>{t('totalVouchers')}</p><strong>{importStatus.totalVouchers}</strong></div>
                <div><p>{t('draft')}</p><strong>{importStatus.draftCount}</strong></div>
                <div><p>{t('approved')}</p><strong>{importStatus.approvedCount}</strong></div>
                <div><p>{t('posted')}</p><strong>{importStatus.postedCount}</strong></div>
              </div>
              {importStatus.lastImportDate && (
                <p className="info-note">
                  {t('lastImport')}: {new Date(importStatus.lastImportDate).toLocaleString(localeKey)}
                </p>
              )}
            </div>
          )}

          {validationRules && (
            <div className="info-card">
              <h3>{t('validationRules')}</h3>
              <div className="info-section">
                <p className="label">{t('requiredColumns')}</p>
                <ul>
                  {validationRules.requiredColumns.map((column) => (<li key={column}>{column}</li>))}
                </ul>
              </div>
              <div className="info-section">
                <p className="label">{t('optionalColumns')}</p>
                <ul>
                  {validationRules.optionalColumns.map((column) => (<li key={column}>{column}</li>))}
                </ul>
              </div>
              <div className="info-section">
                <p className="label">{t('rules')}</p>
                <ul>
                  {validationRules.validationRules.map((rule) => (<li key={rule}>{rule}</li>))}
                </ul>
              </div>
              <div className="info-section note">
                <p>{t('maxFileSize')}: {validationRules.fileLimits.maxSizeMb}MB</p>
                <p>{t('supportedFormats')}: {validationRules.fileLimits.supportedFormats.join(', ')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountingVouchersImportPage;
